import { useState } from "react";
import { Button, Card, ProBadge, Wordmark } from "@cntrl/ui";
import { usePlan, useChangePlan,
  usePlanSettle, useResumePlan, useCancelPlan } from "../../app/hooks/useBilling.js";
import type { PlanState } from "../../app/hooks/useBilling.js";
import { errorMessage } from "../../app/hooks/apiError.js";
import { canChangePlan, planDate, priceLabel, proOptions, standardOption, type PlanOption } from "./planFormat.js";
import styles from "./ProPlanBlock.module.scss";

/**
 * The **Artist CNTRL Pro** upgrade / downgrade surface (ART-142) — ONE surface in two chromes:
 * {@link PlanDialog}, opened by the band's own Settings › Account **Change plan** action, and the roster's
 * Settings › **Artist plan** tab for the artist an org has in focus. Two doors onto one subscription, never
 * two implementations: an org seat and a band seat looking at the same artist are looking at the same
 * subscription, and a second copy is how the two would come to disagree about what a band is paying.
 *
 * **It is split BODY / ACTIONS** ({@link ProPlanBlock} + {@link ProPlanActions}) for the reason the org Team
 * surface is: the two chromes PLACE the controls differently — the dialog pins them in `Modal`'s footer (PO,
 * 2026-09-10), the tab stacks them under the copy — and the state they own (the in-flight mutation, its
 * error, the settle watch) has to live with the buttons. They share the ONE plan read: same query key, one
 * request, and {@link planView} is the one derivation, so the sentence and the buttons cannot disagree about
 * what the band is on.
 *
 * **Neither renders unless `plan.action === "change"`** — the server's single verdict, composed from WHO the
 * caller is (`(root ∈ {artist, management}) AND money access`) and WHICH RAIL the band is on (the Stripe one;
 * never Shopify, never a comp, never lifetime). It is not re-derived here: a rule a client turns into an
 * affordance is a second implementation of that rule, and the routes this calls enforce the same predicates,
 * so the button that is drawn is the call that is accepted.
 *
 * The band's plan is stated on the account tab's **Plan row** in every scenario, action or no action; this is
 * only the part you can DO something with.
 *
 * **The prices are DATA**: every figure comes from `plan.options`, i.e. the `core.plans` row that will be
 * charged. The MVP hard-coded "$250/mo billed annually" into the button while its own registry said $275.
 */

/**
 * Everything both halves derive from the plan read, derived ONCE.
 *
 * The body prints a sentence about the band's tier and the actions draw the buttons that move it; computing
 * "is this band Pro" or "which standard row does a downgrade land on" twice is how a surface comes to offer a
 * downgrade under copy saying the band is already standard.
 */
function planView(data: PlanState) {
  const pro = proOptions(data.options);
  // The standard row on the band's CURRENT cadence — a downgrade must not also change how often they are
  // billed. `planKey` is what they hold now; its interval comes from the same catalogue.
  const currentInterval = (data.options ?? []).find((o) => o.planKey === data.planKey)?.interval;
  return {
    isPro: data.tier === "pro",
    pendingCancel: data.pendingCancel === true,
    pendingPlanKey: data.pendingPlanKey ?? null,
    /** The instant either pending state fires, worded — shared, because both answer the same question. */
    pendingDate: data.pendingPlanKey || data.pendingCancel ? planDate(data.pendingPlanOn) : null,
    planKey: data.planKey ?? null,
    lead: pro[0] as PlanOption | undefined,   // annual first — the server orders the catalogue
    second: pro[1] as PlanOption | undefined,
    standard: standardOption(data.options, currentInterval),
  };
}

/**
 * The BODY: the Pro lockup and the one sentence saying where this band stands. No controls — see
 * {@link ProPlanActions}.
 */
export function ProPlanBlock({ bandId, bandName }: {
  bandId: string | undefined;
  /**
   * The band this subscription belongs to, NAMED (PO, 2026-09-10). Both chromes can be reached with another
   * artist in focus — the roster tab explicitly is — so "this band" leaves the reader to work out which
   * subscription they are about to change. It is a prop rather than a field on the plan read because each
   * chrome already holds the name; absent, the sentence falls back to "This band" rather than a blank.
   */
  bandName?: string | null;
}) {
  const plan = usePlan(bandId);
  const data = plan.data;

  // Loading is silent rather than a spinner: this sits under the account rows on a surface that has already
  // painted, and a skeleton for something most bands will not be offered is more noise than the half-second
  // it covers.
  if (plan.isLoading || !canChangePlan(data) || !data) return null;

  const v = planView(data);
  const subject = bandName?.trim() ? bandName.trim() : "This band";

  return (
    <section className={styles.block} aria-labelledby="pro-plan-heading">
      <h3 id="pro-plan-heading" className={styles.lockup}>
        {/* The real lockup: the Pro chip sits BESIDE the wordmark, never replacing it. */}
        <span className={styles.srOnly}>Artist CNTRL Pro</span>
        <Wordmark size={13} aria-hidden />
        <ProBadge size={15} />
      </h3>

      {v.pendingCancel ? (
        /* ENDING beats every other state, and is worded as a future rather than a past: nothing has been
           taken away yet, and saying "cancelled" to a band that still has a month of Pro would be false.
           No Pro list here: this band is losing the whole app, so naming four Pro features would answer a
           question they are not asking. */
        <p className={styles.copy}>
          {subject}&rsquo;s subscription ends {v.pendingDate}. They keep everything until then.
        </p>
      ) : v.isPro ? (
        <>
          <p className={styles.copy}>
            {subject} is on <b>Artist CNTRL Pro</b>
            {v.pendingPlanKey ? <> — downgrade scheduled; Pro runs through {v.pendingDate}.</> : "."}
          </p>
          <ProLosses on={v.pendingPlanKey ? v.pendingDate : null} />
        </>
      ) : (
        <p className={styles.copy}>
          Teams &amp; crew roles, Buses, department-tabbed Advance Docs with shareable venue links — built for
          artists playing 3K+ rooms.
        </p>
      )}
    </section>
  );
}

/**
 * What a Pro band gives up by downgrading, named one line at a time (PO, 2026-09-10).
 *
 * The dialog used to be a sentence and two controls, and it asked for a decision without telling anyone what
 * the decision costs. This is the answer, and every line is a surface that is BUILT and gated today rather
 * than a roadmap: parties (ART-144), the advance catalogue with its twelve departments, and the link password
 * and expiry (both ART-146). Buses, party chats and the Pro mic list are not in here for exactly that reason,
 * though the upgrade copy one branch up still advertises Buses.
 *
 * Kept to LABELS rather than sentences (PO, 2026-09-11). A band deciding whether to keep paying should take
 * the whole panel in at a glance; four explanatory two-part bullets of matching length read as generated
 * however plain the words are, and nobody reads them anyway.
 *
 * **It says what SURVIVES, in the body rather than only in the confirm.** "A downgrade must never destroy
 * data" is the rule the whole tier axis is built on, and a reader who only learns it from a `window.confirm`
 * learns it after they have already decided.
 */
function ProLosses({ on }: {
  /** The date a SCHEDULED downgrade fires, or null when it is still hypothetical. A band that has already
   *  pressed the button is asking "what happens on the 12th", not "what would happen". */
  on: string | null;
}) {
  // The `Card` primitive, not a hand-rolled panel: paper on the modal's canvas ground is exactly the
  // raised relationship it draws on a page, and the one place this repo's card chrome is decided. Headerless
  // on purpose — the lead below is a sentence, and a card TITLE would put a second `<h3>` under the lockup.
  return (
    <Card className={styles.losses}>
      {/* One interpolated string rather than a fragment: an element whose sentence is split across three
          text nodes cannot be matched as a sentence, by a test or by a screen reader's phrase navigation. */}
      <p className={styles.lossLead}>
        {on ? `From ${on}, this turns off:` : "Downgrading to standard turns off:"}
      </p>
      {/* Labels, not sentences (PO wording, verbatim). No trailing periods: these name the things that go,
          and a full stop on a three-word label is what turns a list into prose nobody reads.
          No COUNT on the departments (PO, 2026-09-11) — Pro's twelve against Standard's seven is a figure
          that has to be re-checked every time either vocabulary moves, and it was never the reason anyone
          upgrades. */}
      <ul className={styles.lossList}>
        <li>Crew parties</li>
        <li>Advance Docs per tour/festival</li>
        <li>Extra department tabs</li>
        <li>Passwords and expiry dates on venue links</li>
      </ul>
      <p className={styles.lossKeep}>
        Your work on Pro is saved, so if you decide to upgrade again, it&rsquo;ll still be here.
      </p>
    </Card>
  );
}

/**
 * The ACTIONS: every control this surface has, plus the two things that can only be said once one has been
 * pressed (the mutation's refusal, and a payment whose plan never landed).
 *
 * In the dialog it IS the footer — `align="end"`, so the row sits at the card's trailing edge with
 * **Cancel subscription** left of the primary move (PO, 2026-09-10). Inline under the roster tab it stacks
 * left under the copy.
 */
export function ProPlanActions({
  bandId,
  align = "start",
  // Injected for the same reason `ActivateScreen` injects its own: a full-page navigation and a confirm
  // dialog are the two things a component cannot exercise in a test, and the alternative — stubbing
  // `window.location` — replaces the object every other part of the render reads.
  onCheckoutUrl = (url: string) => window.location.assign(url),
  confirmAction = (message: string) => window.confirm(message),
}: {
  bandId: string | undefined;
  /** Where the row sits: `end` in the modal footer, `start` inline under the copy. */
  align?: "start" | "end";
  onCheckoutUrl?: (url: string) => void;
  /** Confirms the two destructive moves — downgrading, and ending the subscription. */
  confirmAction?: (message: string) => boolean;
}) {
  const plan = usePlan(bandId);
  const change = useChangePlan(bandId);
  const resume = useResumePlan(bandId);
  const cancel = useCancelPlan(bandId);
  const [swapped, setSwapped] = useState(false);
  // ABOVE the early returns below — every hook in this component must run on every render, and putting this
  // beside its first USE (after the `action !== "change"` guard) changed the hook count between renders.
  // The webhook is part of the OPERATION, not a state the reader has to understand: `settling` keeps the
  // control busy through the second or two `plan_key` takes to land, so the surface simply re-renders as Pro
  // instead of going idle under copy explaining why it had not. See usePlanSettle.
  const settle = usePlanSettle(bandId);

  const data = plan.data;
  // One verdict, the server's. `change` means the Stripe rail AND a caller who may act; every other value —
  // a Shopify band, a comp, a lifetime, a booking agent — is answered by the Plan ROW, which states the plan
  // and says why there is nothing here. This guard is defence in depth: the dialog is only ever opened from
  // an action that already means `change`, and the roster tab is gated on the same value.
  if (plan.isLoading || !canChangePlan(data) || !data) return null;

  const v = planView(data);
  const busy = change.isPending || resume.isPending || cancel.isPending || settle.settling;
  // The LABEL has to move with the busy state, not just `disabled`. Widening only the latter left the button
  // inert while still reading "Upgrade · $250/mo billed annually" — a dead control wearing its idle words,
  // which is worse than the copy it replaced. Only the two Pro buttons: a swap is the one change whose
  // outcome a webhook confirms, so it is the only one that can still be working after its request returned.
  // Which one is working, not merely that something is. Both Pro buttons reading "Working…" says the app is
  // busy without saying at what — and they are two different purchases. `variables` is the plan key the
  // in-flight mutation carries, and it survives into the settle window because the mutation has resolved.
  const working = change.isPending || settle.settling;
  const workingKey = working ? change.variables : undefined;
  const error = change.error ?? resume.error ?? cancel.error;

  /** Both purchase buttons do the same thing with the answer — a redirect, or a swap that settles. */
  const buy = (planKey: string) =>
    change.mutate(planKey, {
      onSuccess: (r) => {
        // A band with no live subscription is BUYING, so it redirects. One that already pays gets a price
        // swap with no redirect at all — pointing this at checkout instead would leave them holding two live
        // subscriptions.
        if (r.checkoutUrl) onCheckoutUrl(r.checkoutUrl);
        else if (r.applied) setSwapped(true);
      },
    });

  return (
    <div className={styles.actions} data-align={align}>
      {swapped && settle.unconfirmed && (
        // THE ONLY BRANCH THAT SPEAKS, and the only one that has to: the payment went through and the plan
        // did not follow inside the window, so something is actually broken — a webhook endpoint that is not
        // routed, a signing secret that does not match, a delivery outage. A band whose money moved must be
        // told that plainly rather than left watching a spinner. While it is merely SETTLING this renders
        // nothing; the busy button is the whole of the feedback.
        <p className={styles.notice} role="alert">
          Payment taken, but we couldn&rsquo;t confirm the change. Nothing is lost — reload, and contact
          support if the plan still hasn&rsquo;t changed.
        </p>
      )}

      {/* No spinner: every control already says "Working…" while it is pending, and a second live region
          announcing the same state is noise to a screen reader rather than reassurance. */}
      {error && (
        <p className={styles.error} role="alert">
          {errorMessage(error, "That didn't work. Please try again.")}
        </p>
      )}

      <div className={styles.row}>
        {/* Ending the subscription — ART-152's half, and the thing no band on any tier could do through the
            product until now. Deliberately a quiet link rather than a button, and it leads the row so the
            primary move keeps the trailing edge: a destructive action given equal visual weight to the
            constructive one reads as a choice of two. Absent for a band with nothing to end (born blank, or
            already ending). */}
        {!v.pendingCancel && v.planKey && (
          <button
            type="button"
            className={styles.cancelLink}
            disabled={busy}
            onClick={() => {
              const ok = confirmAction(
                "End this subscription at the end of the paid period? You keep everything until then, and " +
                  "the app locks after that until you subscribe again.",
              );
              if (ok) cancel.mutate();
            }}
          >
            {cancel.isPending ? "Working…" : "Cancel subscription"}
          </button>
        )}

        {v.pendingCancel ? (
          <Button variant="ghost" disabled={busy} onClick={() => resume.mutate()}>
            {resume.isPending ? "Working…" : "Keep subscription"}
          </Button>
        ) : v.isPro ? (
          v.pendingPlanKey ? (
            <Button variant="ghost" disabled={busy} onClick={() => resume.mutate()}>
              {resume.isPending ? "Working…" : "Keep Pro"}
            </Button>
          ) : (
            <Button
              variant="ghost"
              disabled={busy || !v.standard}
              onClick={() => {
                const standard = v.standard;
                if (!standard) return;
                // Confirmed because it is the one direction that takes something away — and the promise that
                // the DATA survives is the whole of the downgrade rule, so it is what the prompt says.
                const ok = confirmAction(
                  "Downgrade to standard Artist CNTRL at the end of the paid period? Pro features stop " +
                    "rendering — the data is kept and comes back if you re-upgrade.",
                );
                if (ok) change.mutate(standard.planKey);
              }}
            >
              {change.isPending ? "Working…" : "Downgrade to standard"}
            </Button>
          )
        ) : (
          <>
            {v.second && (
              <Button variant="ghost" disabled={busy} onClick={() => buy(v.second!.planKey)}>
                {workingKey === v.second.planKey ? "Working…" : priceLabel(v.second)}
              </Button>
            )}
            {v.lead && (
              <Button disabled={busy} onClick={() => buy(v.lead!.planKey)}>
                {workingKey === v.lead.planKey ? "Working…" : `Upgrade · ${priceLabel(v.lead)}`}
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
